import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { genreOptions, regionOptions, yearOptions } from "@/mocks/media";
import { filterMedia, type SortKey } from "@/utils/media";
import MediaCard from "@/components/feature/MediaCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import FilterPanel from "@/pages/category/components/FilterPanel";

const PAGE_SIZE = 8;
const LOAD_STEP = 4;

const typeMeta = {
  movie: { label: "电影", icon: "ri-film-line", desc: "汇集最新院线大片、口碑佳作与经典影片" },
  tv: { label: "电视剧", icon: "ri-tv-2-line", desc: "热播剧集、全集内容与高分口碑剧" },
} as const;

type CategoryType = keyof typeof typeMeta;

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const meta = type && type in typeMeta ? typeMeta[type as CategoryType] : undefined;

  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [sort, setSort] = useState<SortKey>("published");
  const [page, setPage] = useState(1);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [viewMode, setViewMode] = useState<"page" | "more">("page");

  useSEO({
    title: meta ? `${meta.label}分类 - 麻豆精品` : "分类 - 麻豆精品",
    description: meta
      ? `麻豆精品${meta.label}分类页，按题材、地区、年份多维筛选，快速找到想看的${meta.label}作品。`
      : "麻豆精品影视分类页。",
    keywords: `麻豆精品,${meta ? meta.label : "影视"},影视分类,${genreOptions.slice(0, 5).join(",")}`,
  });

  const filtered = useMemo(
    () =>
      filterMedia({
        type: meta ? (type as CategoryType) : undefined,
        genres: selectedGenres,
        regions: selectedRegions,
        years: selectedYears,
        sort,
      }),
    [meta, type, selectedGenres, selectedRegions, selectedYears, sort],
  );

  useEffect(() => {
    setPage(1);
    setVisibleCount(PAGE_SIZE);
  }, [selectedGenres, selectedRegions, selectedYears, sort, type]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const loadItems = filtered.slice(0, visibleCount);
  const items = viewMode === "page" ? pageItems : loadItems;

  const activeCount = selectedGenres.length + selectedRegions.length + selectedYears.length;

  const resetFilters = () => {
    setSelectedGenres([]);
    setSelectedRegions([]);
    setSelectedYears([]);
    setSort("published");
  };

  const toggle = <T,>(list: T[], value: T, setter: (next: T[]) => void) => {
    setter(list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);
  };

  if (!meta) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-error-warning-line"
          title="未找到该分类"
          description="请检查链接是否正确，或返回首页浏览全部影视内容。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "分类" }, { label: `${meta.label}分类` }]} />

      <header className="mt-5">
        <h1 className="flex items-center gap-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className={`${meta.icon} text-xl`} aria-hidden="true"></i>
          </span>
          {meta.label}分类
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
          {meta.desc}，共收录 <strong className="font-semibold text-primary-600">{filtered.length}</strong> 部作品。
        </p>
      </header>

      <div className="mt-6">
        <FilterPanel
          genreOptions={genreOptions}
          regionOptions={regionOptions}
          yearOptions={yearOptions}
          selectedGenres={selectedGenres}
          selectedRegions={selectedRegions}
          selectedYears={selectedYears}
          sort={sort}
          onToggleGenre={(value) => toggle(selectedGenres, value, setSelectedGenres)}
          onToggleRegion={(value) => toggle(selectedRegions, value, setSelectedRegions)}
          onToggleYear={(value) => toggle(selectedYears, value, setSelectedYears)}
          onSortChange={setSort}
          onReset={resetFilters}
          activeCount={activeCount}
        />
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-foreground-600">
          共 <strong className="font-semibold text-foreground-900">{filtered.length}</strong> 个结果
        </p>
        <div className="flex items-center gap-1 rounded-full bg-background-200 p-1">
          <button
            type="button"
            onClick={() => setViewMode("page")}
            className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              viewMode === "page" ? "bg-background-50 text-foreground-900" : "text-foreground-600"
            }`}
          >
            分页浏览
          </button>
          <button
            type="button"
            onClick={() => setViewMode("more")}
            className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              viewMode === "more" ? "bg-background-50 text-foreground-900" : "text-foreground-600"
            }`}
          >
            加载更多
          </button>
        </div>
      </div>

      {items.length > 0 ? (
        <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
          {items.map((item) => (
            <MediaCard key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <div className="mt-5">
          <EmptyState
            icon="ri-search-eye-line"
            title="没有符合条件的内容"
            description="试试减少筛选条件，或重置后浏览全部作品。"
            actionLabel="重置筛选"
            onAction={resetFilters}
          />
        </div>
      )}

      {viewMode === "page" && items.length > 0 && (
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      )}

      {viewMode === "more" && visibleCount < filtered.length && (
        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={() => setVisibleCount((prev) => prev + LOAD_STEP)}
            className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:bg-primary-50"
          >
            <i className="ri-add-line" aria-hidden="true"></i>
            加载更多（剩余 {filtered.length - visibleCount} 部）
          </button>
        </div>
      )}
    </div>
  );
}